import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import * as lan from '../common/script/common_lan.js';
import axios from 'axios';
import { showAlert } from '../common/script/common';
import { loadTossPayments, ANONYMOUS } from "@tosspayments/tosspayments-sdk";

const hostname = window.location.hostname;
let clientKey;
if (hostname === 'qa-app.thekgolf.com') {
	clientKey = 'test_gck_26DlbXAaV0M0eElj2GX5rqY50Q9R';
} else if (hostname === 'test-app.thekgolf.com') {
	clientKey = 'test_gck_26DlbXAaV0M0eElj2GX5rqY50Q9R';
} else if (hostname === 'app.thekgolf.com') {
	clientKey = 'live_gck_Z1aOwX7K8mvKKbDlwXYQ3yQxzvNP';
} else {
	clientKey = 'test_gck_26DlbXAaV0M0eElj2GX5rqY50Q9R';
}

// 샘플
const payment = {
	currency: 'KRW',
	amount: 12345,
	id: 'SAMPLE_ITEM_ID'
};

// lang 파라미터 -> sessionStorage 'Language' 코드, API 헤더 코드 매핑
function resolveLanguage(param) {
	const p = (param || '').toLowerCase();
	switch (p) {
		case 'en-us': return { ui: 'en', header: 'en-us' };
		case 'zh-cn': return { ui: 'zh', header: 'zh-cn' };
		case 'vi-vn': return { ui: 'vi', header: 'vi-vn' };
		case 'ja-jp': return { ui: 'ja', header: 'ja-jp' };
		case 'zh-tw': return { ui: 'tw', header: 'zh-tw' };
		case 'ko-kr':
		default:      return { ui: 'ko', header: 'ko-kr' };
	}
}

export default function Payment() {
	const location = useLocation();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);
	const [isReady, setIsReady] = useState(false);
	const [infoItems, setInfoItems] = useState([]);

	const [widgets, setWidgets] = useState(null);
	const [isWidgetsReady, setIsWidgetsReady] = useState(false);
	const [amount, setAmount] = useState({ currency: "KRW", value: 50000 });
	const [orderInfo, setOrderInfo] = useState(null);

	// 모달 상태
	const [showPayModal, setShowPayModal] = useState(false);
	const [agreed, setAgreed] = useState(false);
	const [isRendering, setIsRendering] = useState(false);

	// Toss 위젯 중복 렌더 방지
	const hasRenderedRef = useRef(false);

	// API 헤더에 넣을 언어 (기본 ko-kr)
	const [headerLang, setHeaderLang] = useState('ko-kr');

	// === 언어 파라미터 처리 (최초 진입 시) ===
	useEffect(() => {
		const qp = new URLSearchParams(location.search);
		const langParam = qp.get('lang');
		const { ui, header } = resolveLanguage(langParam);
		sessionStorage.setItem('Language', ui);
		setHeaderLang(header);
	}, [location.search]);

	// Body 스크롤 락/해제
	useEffect(() => {
		if (!showPayModal) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => { document.body.style.overflow = prev; };
	}, [showPayModal]);

	// ESC로 모달 닫기
	useEffect(() => {
		const onKey = (e) => { if (e.key === 'Escape') setShowPayModal(false); };
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	// TossPayments 초기화 (한 번만)
	useEffect(() => {
		(async () => {
			const tossPayments = await loadTossPayments(clientKey);
			const w = tossPayments.widgets({ customerKey: ANONYMOUS });
			setWidgets(w);
			setIsWidgetsReady(true);
		})();
	}, []);

	// 모달 열릴 때: 최초 1회만 render, 이후에는 setAmount만 갱신
	useEffect(() => {
		const renderPaymentWidgetsInModal = async () => {
			if (!showPayModal || !widgets || isRendering) return;

			setIsRendering(true);
			try {
				await widgets.setAmount(amount);

				if (!hasRenderedRef.current) {
					const agreementKey = (amount.currency !== 'KRW') ? 'agreement-en' : 'AGREEMENT';
					const agreementWidget = await widgets.renderAgreement({
						selector: "#agreement",
						variantKey: agreementKey,
					});
					agreementWidget.on("agreementStatusChange", (status) => {
						setAgreed(status.agreedRequiredTerms);
					});
					setAgreed(true);
					await widgets.renderPaymentMethods({
						selector: "#payment-method",
						variantKey: amount.currency === 'KRW' ? "DomesticPayment" : "InternationalPayment",
					});

					hasRenderedRef.current = true;
				}
			} finally {
				setIsRendering(false);
			}
		};

		if (showPayModal) renderPaymentWidgetsInModal();
	}, [showPayModal, widgets, amount, isRendering]);

	// 서버 스크립트/화면 초기화 (기존 로직 유지 + 다국어 텍스트)
	useEffect(() => {
		const fetchAndPrepareScript = async () => {
			try {
				const queryParams = new URLSearchParams(location.search);
				const clientGuid   = queryParams.get("clientGUID");
				const gameType     = queryParams.get("gameType");
				const confirmKey   = queryParams.get("confirmkey");
				const gameHoleType = queryParams.get("gameHoleType");
				const playerNumber = queryParams.get("playerNumber");
				const coursePlanId = queryParams.get("coursePlanId");

				if (!clientGuid || !gameType || !confirmKey || !gameHoleType || !playerNumber || !coursePlanId) {
					throw new Error('Missing required query parameters');
				}

				// 게임 타입 텍스트 (lan.L로 래핑)
				const gameTypeText =
					gameType === "0" ? lan.L('스트로크') :
						gameType === "5" ? lan.L('대회') :
							gameType === "1" ? lan.L('포썸') :
								gameType === "2" ? lan.L('3vs3') :
									gameType === "3" ? lan.L('2vs2vs2') : lan.L('스크램블');

				// 홀 타입 텍스트 (lan.L로 래핑)
				const gameHoleTypeText =
					gameHoleType === "0" ? lan.L('18홀 - 전반홀 > 후반홀') :
						gameHoleType === "1" ? lan.L('18홀 - 후반홀 > 전반홀') :
							gameHoleType === "2" ? lan.L('9홀 - 전반홀') : lan.L('9홀 - 후반홀');

				// 인원수는 숫자만 표시 (라벨은 다국어)
				setInfoItems([
					{ title: lan.L('게임 타입'), content: gameTypeText },
					{ title: lan.L('홀 타입'), content: gameHoleTypeText },
					{ title: lan.L('인원수'), content: String(playerNumber) }
				]);

				setIsReady(true);
			} catch (error) {
				console.error(error);
				showAlert(lan.L('Payment initialization failed'));
				navigate('/error');
			} finally {
				setLoading(false);
			}
		};

		fetchAndPrepareScript();
	}, [location, navigate]);

	// 모달 열기 + 주문 생성
	const handleStartPayment = useCallback(async () => {
		if (!isWidgetsReady || !widgets) {
			showAlert(lan.L('결제 위젯을 준비 중입니다. 잠시 후 다시 시도해 주세요.'));
			return;
		}

		try {
			const queryParams = new URLSearchParams(location.search);
			const clientGuid   = queryParams.get("clientGUID");
			const gameType     = queryParams.get("gameType");
			const confirmKey   = queryParams.get("confirmkey");
			const gameHoleType = queryParams.get("gameHoleType");
			const playerNumber = queryParams.get("playerNumber");
			const coursePlanId = queryParams.get("coursePlanId");

			if (!clientGuid || !gameType || !confirmKey || !gameHoleType || !playerNumber || !coursePlanId) {
				throw new Error('Missing required query parameters');
			}

			const isQA = window.location.hostname.startsWith('qa-app.thekgolf.com');
			// 필요 시 실서버 분기 추가
			const billingHost = isQA ? 'qa-billing.thekgolf.com' : 'billing.thekgolf.com';

			const url = `https://${billingHost}/v3/payment/toss/createOrder/course`
				+ `?clientGuid=${clientGuid}`
				+ `&gameType=${gameType}`
				+ `&confirmkey=${confirmKey}`
				+ `&gameHoleType=${gameHoleType}`
				+ `&playerNumber=${playerNumber}`
				+ `&coursePlanId=${coursePlanId}`;

			// 주문 생성
			const res = await axios.post(url, {
				itemId: payment.id,
				totalPrice: payment.amount,
				quantity: 1
			}, {
				headers: {
					'Content-Type': 'application/json',
					'x-thekgolf-timezone': '+09:00',
					'x-thekgolf-language': headerLang
				}
			});

			const { data } = res;

			if (typeof data === 'string') {
				alert(data.trim());
				return; // ⛔ 아래 로직 진행 금지
			}

			const totalValue = Math.floor(res.data.price);
			const orderId    = res.data.orderNumber;
			const currency   = res.data.shopDetails.currency;
			const orderName  = res.data.btItemName;

			// 금액/통화 상태에도 반영 (모달 헤더 표시용 + setAmount 트리거)
			setAmount({ currency, value: totalValue });

			// 위젯 금액 즉시 갱신 (안전가드)
			if (widgets?.setAmount) {
				await widgets.setAmount({ currency, value: totalValue });
			}

			// requestPayment에 사용할 payload 저장
			setOrderInfo({
				orderId,
				orderName,
				customerName:  res.data.shopDetails.ownerName,
				customerEmail: res.data.shopDetails.ownerEmail,
				successUrl: window.location.origin + "#/pay/course/success",
				failUrl:    window.location.origin + "#/pay/course/fail",
				pendingUrl: window.location.origin + "#/pay/course/pending",
			});

			hasRenderedRef.current = false;
			setShowPayModal(true);
		} catch (err) {
			console.error(err);
			showAlert(lan.L('Payment initialization failed'));
		}
	}, [isWidgetsReady, widgets, location.search, headerLang]);

	// 모달 내부 “결제하기”
	const handleTossPay = useCallback(async () => {
		if (!widgets) {
			showAlert(lan.L('결제 위젯이 준비되지 않았습니다.'));
			return;
		}
		if (!orderInfo?.orderId) {
			showAlert(lan.L('결제 정보가 유효하지 않습니다. 다시 시도해 주세요.'));
			return;
		}

		try {
			await widgets.requestPayment(orderInfo);
		} catch (err) {
			console.error(err);
			showAlert(lan.L('Payment failed or cancelled'));
		}
	}, [widgets, orderInfo]);

	return (
		<>
			<div className="login_head"><span></span></div>

			{/* 기존 컨텐츠 */}
			<div id="payment-container" style={{textAlign: 'center'}}></div>

			<div style={{
				backgroundImage: "url(" + require('../images/bg.png') + ")",
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				height: 'calc(100vh - 60px)',
				display: 'flex',
				flexDirection: 'column',
				color: '#fff',
				textAlign: 'center',
				padding: '1.5rem',
				marginTop: '0.8rem'
			}}>
				<div style={{flex: 1}}></div>

				{isReady && (
					<div style={{
						backgroundColor: 'rgba(0, 0, 0, 0.4)',
						borderRadius: '8px',
						width: '90%',
						maxWidth: '320px',
						boxSizing: 'border-box',
						display: 'flex',
						flexDirection: 'column',
						margin: '0 auto'
					}}>
						{infoItems.map((item, index) => (
							<div key={index} style={{
								display: 'flex',
								borderTop: index !== 0 ? '1px solid rgba(255, 255, 255, 0.5)' : 'none'
							}}>
								<div style={{
									flex: 1,
									borderRight: '1px solid rgba(255, 255, 255, 0.5)',
									padding: '0.75rem',
									color: '#fff',
									fontSize: '0.9rem',
									textAlign: 'left',
									whiteSpace: 'pre-line'
								}}>
									{item.title}
								</div>
								<div style={{
									flex: 2,
									padding: '0.75rem',
									color: '#fff',
									fontSize: '0.9rem',
									textAlign: 'right',
									whiteSpace: 'pre-line'
								}}>
									{item.content}
								</div>
							</div>
						))}
					</div>
				)}

				{isReady && (
					<div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
						<button
							onClick={handleStartPayment}
							disabled={loading}
							style={{
								backgroundColor: '#1D4ED8',
								color: '#ffffff',
								fontSize: '1.125rem',
								padding: '0.75rem 2rem',
								border: 'none',
								borderRadius: '9999px',
								boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
								cursor: 'pointer'
							}}
						>
							{lan.L('코스 결제 진행')}
						</button>
					</div>
				)}

				<p style={{textAlign: 'left'}}>* {lan.L('결제전에 게임 정보를 확인해 주세요.')}</p>
				<p style={{textAlign: 'left', marginTop: '0.5rem'}}> {lan.L('콘텐츠 특성상 게임이 시작되면 환불이 불가능 합니다.')}</p>
				<p style={{textAlign: 'left', marginTop: '1rem'}}> * {lan.L('결제후 게임이 실행되지 않을 경우,')}</p>
				<p style={{textAlign: 'left', marginTop: '0.5rem'}}> {lan.L('키오스크 화면에서 ‘게임시작요청’ 버튼을 터치해 주세요.')}</p>
				<p style={{textAlign: 'left', marginTop: '1rem'}}> * {lan.L('환불 관련 문의는 고객센터(1577-0564)에서 진행하실 수 있습니다.')}</p>
				<div style={{flex: 0.4}}></div>
			</div>

			{/* ===== 모달(overlay) — DOM 유지, display만 토글 ===== */}
			<div
				role="dialog"
				aria-modal="true"
				onClick={(e) => {
					if (e.target === e.currentTarget) setShowPayModal(false);
				}}
				style={{
					position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
					display: showPayModal ? 'flex' : 'none',
					alignItems: 'center', justifyContent: 'center', zIndex: 9999
				}}
			>
				<div
					className="payment_button_wrap"
					style={{
						width: 'min(720px, 92vw)',
						maxHeight: '80vh',
						background: '#fff',
						borderRadius: '16px',
						overflow: 'hidden',
						display: 'grid',
						gridTemplateRows: 'auto 1fr auto'
					}}
				>
					{/* 헤더 (고정) */}
					<div
						style={{
							padding: '14px 18px',
							borderBottom: '1px solid #eee',
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center'
						}}
					>
						<strong style={{fontSize: 16, color: '#111'}}>
							{lan.L('코스 결제')} : {amount.value.toLocaleString(amount.currency === 'KRW' ? 'ko-KR' : 'en-US')}
							{amount.currency === 'KRW' ? ` ${lan.L('원')}` : ` ${amount.currency}`}
						</strong>
						<button
							onClick={() => setShowPayModal(false)}
							style={{border: 0, background: 'transparent', fontSize: 18, cursor: 'pointer'}}
							aria-label="Close"
							title="Close"
						>
							✕
						</button>
					</div>

					{/* 스크롤 영역 (약관/결제수단 전부 포함) */}
					<div
						className="wrapper w-100"
						style={{
							minHeight: 0,
							overflowY: 'auto',
							padding: 20,
							WebkitOverflowScrolling: 'touch'
						}}
					>
						<div className="max-w-540 w-100">
							<div id="payment-method" className="w-100"/>
							<div id="agreement" className="w-100"/>
						</div>
					</div>

					{/* 푸터(결제 버튼) - 항상 보임 */}
					<div
						className="btn-wrapper w-100"
						style={{
							padding: 12,
							borderTop: '1px solid #eee',
							background: '#fff'
						}}
					>
						<button
							className="btn primary w-100"
							disabled={!agreed || loading || !orderInfo?.orderId}
							onClick={handleTossPay}
							style={{
								width: '100%',
								padding: '12px 16px',
								background: (!agreed || loading || !orderInfo?.orderId) ? '#9CA3AF' : '#1D4ED8',
								color: '#fff',
								border: 0,
								borderRadius: 8,
								cursor: (!agreed || loading || !orderInfo?.orderId) ? 'not-allowed' : 'pointer'
							}}
						>
							{lan.L('결제하기')}
						</button>
					</div>
				</div>
			</div>
		</>
	);
}
